import {memo, ReactNode} from "react";
import {Box, HStack, Icon, Stack, Table, Text} from "@chakra-ui/react";
import {KeyIcon} from "@heroicons/react/16/solid";
import {FilterBar} from "./FilterBar";
import {RecordColumn} from "../pages/populationsData";

/**
 * A floating table of records for a given schema — used by the population Data tab and by a
 * sample's Sampled-data / Source-data tabs. Right-aligns numeric columns with tabular figures.
 * An optional `header` renders as a band attached to the top of the table card.
 */
export const RecordsTable = memo(
  ({schema, rows, header}: {schema: RecordColumn[]; rows: Record<string, string>[]; header?: ReactNode}) => (
    <Stack gap={0} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden" bg="white">
      {header && (
        <Box borderBottom="1px solid" borderColor="gray.200">
          {header}
        </Box>
      )}
      <FilterBar total={rows.length} />
      <Table.Root
        interactive
        className="spacious"
        css={{
          "& tbody tr:last-of-type td": {borderBottom: "none"},
          "& th:first-of-type, & td:first-of-type": {paddingInlineStart: "var(--chakra-spacing-6)"},
          "& th:last-of-type, & td:last-of-type": {paddingInlineEnd: "var(--chakra-spacing-6)"},
        }}
      >
        <Table.Header>
          <Table.Row>
            {schema.map(col => (
              <Table.ColumnHeader key={col.key} color="gray.500" textAlign={col.numeric ? "end" : "start"}>
                {col.isKey ? (
                  <HStack as="span" gap={1} display="inline-flex" align="center">
                    {col.label}
                    <Icon asChild boxSize="3.5" color="gray.400">
                      <KeyIcon />
                    </Icon>
                  </HStack>
                ) : (
                  col.label
                )}
              </Table.ColumnHeader>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((record, i) => (
            <Table.Row key={i}>
              {schema.map(col => (
                <Table.Cell key={col.key} textAlign={col.numeric ? "end" : "start"}>
                  <Text
                    fontSize="sm"
                    color="fg.muted"
                    fontWeight={col.isKey ? "600" : undefined}
                    fontVariantNumeric={col.numeric ? "tabular-nums" : undefined}
                  >
                    {record[col.key]}
                  </Text>
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Stack>
  ),
);
